import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPaperPlane } from 'react-icons/fa';

const QnaWrite = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    isPrivate: false
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  // 카테고리 목록
  const categories = ['서비스 이용', '공양 문의', '사찰 문의', '계정 관련', '기타'];
  
  // 인증 상태 확인
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('accessToken');
      const isAuth = !!token;
      setIsAuthenticated(isAuth);
      
      // 로그인되지 않은 경우 로그인 페이지로 리디렉션
      if (!isAuth) {
        navigate('/login', { state: { from: '/qna/write' } });
      }
    };
    
    checkAuth();
  }, [navigate]);
  
  // 입력 핸들러
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // 입력 시 해당 필드의 에러 메시지 제거
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  // 유효성 검사
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = '제목을 입력해주세요.';
    }
    
    if (!formData.content.trim()) {
      newErrors.content = '내용을 입력해주세요.';
    }
    
    if (!formData.category) {
      newErrors.category = '카테고리를 선택해주세요.';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // 폼 제출 핸들러
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 유효성 검사
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // 실제 API 호출 대신 타임아웃으로 시뮬레이션
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 성공 시
      setSubmitSuccess(true);
      
      // 3초 후 목록 페이지로 리디렉션
      setTimeout(() => {
        navigate('/qna');
      }, 3000);
      
    } catch (err) {
      console.error('Q&A 작성 오류:', err);
      setSubmitError('Q&A 작성 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleCancel = () => {
    navigate(-1);
  };
  
  return (
    <div className="qna-write py-5">
      <Container>
        <div className="mb-4">
          <Button 
            variant="outline-secondary" 
            onClick={handleCancel}
            className="rounded-pill"
          >
            <FaArrowLeft className="me-2" /> 뒤로가기
          </Button>
        </div>
        
        <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-4">
          <Card.Header className="bg-white border-bottom py-3 px-4">
            <h1 className="fs-4 mb-0 fw-bold">Q&A 작성하기</h1>
          </Card.Header>
          
          <Card.Body className="p-4">
            {submitSuccess ? (
              <Alert variant="success">
                <h5 className="alert-heading">질문이 성공적으로 등록되었습니다!</h5>
                <p>답변이 등록되면 알림을 드립니다. 잠시 후 Q&A 목록 페이지로 이동합니다.</p>
              </Alert>
            ) : (
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">카테고리</Form.Label>
                  <Form.Select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    isInvalid={!!errors.category}
                    className="rounded-3"
                  >
                    <option value="">카테고리 선택</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.category}
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">제목</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    placeholder="질문의 제목을 입력하세요"
                    value={formData.title}
                    onChange={handleChange}
                    isInvalid={!!errors.title}
                    className="rounded-3"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.title}
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">내용</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="content"
                    placeholder="질문 내용을 자세히 작성해주세요"
                    value={formData.content}
                    onChange={handleChange}
                    isInvalid={!!errors.content}
                    className="rounded-3"
                    style={{ minHeight: '200px' }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.content}
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-4">
                  <Form.Check
                    type="checkbox"
                    name="isPrivate"
                    label="비밀글로 작성하기 (관리자와 작성자만 확인 가능)"
                    checked={formData.isPrivate}
                    onChange={handleChange}
                  />
                </Form.Group>
                
                {submitError && (
                  <Alert variant="danger" className="mb-4">
                    {submitError}
                  </Alert>
                )}
                
                <div className="d-flex justify-content-center gap-2">
                  <Button 
                    variant="secondary" 
                    onClick={handleCancel}
                    className="px-4 rounded-pill"
                    disabled={isSubmitting}
                  >
                    취소
                  </Button>
                  <Button 
                    variant="primary" 
                    type="submit"
                    className="px-4 rounded-pill"
                    style={{ 
                      backgroundColor: 'var(--main-color)', 
                      borderColor: 'var(--main-color)',
                      boxShadow: '0 4px 10px rgba(0,149,144,0.2)'
                    }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? '등록 중...' : (
                      <>
                        <FaPaperPlane className="me-2" /> 등록하기
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default QnaWrite;